import { useState, useEffect } from "react";
import { useLocation, Redirect } from "wouter";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Bot, Loader2, ArrowLeft, Mail, AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { SiGoogle } from "react-icons/si";

const OAUTH_ERROR_MESSAGES: Record<string, string> = {
  domain: "Accès réservé aux comptes Jobotto (@jobotto.fr)",
  not_allowed: "Votre compte n'est pas encore activé. Contactez l'administrateur.",
  check_failed: "Impossible de vérifier votre accès. Réessayez.",
};

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetSent, setResetSent] = useState(false);
  const [oauthError, setOauthError] = useState<string | null>(null);
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { isAuthenticated, isLoading } = useAuth();
  const queryClient = useQueryClient();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const err = params.get("error");
    if (err) setOauthError(OAUTH_ERROR_MESSAGES[err] || "Erreur de connexion Google.");
  }, []);

  if (isAuthenticated) {
    return <Redirect to="/" />;
  }

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    setOauthError(null);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
        queryParams: { hd: "jobotto.fr" },
      },
    });
    if (error) {
      setOauthError(error.message);
      setGoogleLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      toast({
        variant: "destructive",
        title: "Connexion échouée",
        description: error.message,
      });
      setLoading(false);
    } else {
      queryClient.setQueryData(['auth', 'session'], data.session);
      queryClient.invalidateQueries({ queryKey: ['/api/auth/me'] });
      let redirectDest = "/";
      try {
        const saved = sessionStorage.getItem("redirectAfterLogin");
        sessionStorage.removeItem("redirectAfterLogin");
        if (saved && saved.startsWith("/") && !saved.startsWith("//")) {
          redirectDest = saved;
        }
      } catch (err) {
        console.error("[LoginPage] sessionStorage read failed:", err);
      }
      setLocation(redirectDest);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const redirectTo = `${window.location.origin}/reset-password`;
    const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
      redirectTo,
    });

    setLoading(false);

    if (error) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message,
      });
    } else {
      setResetSent(true);
      toast({
        title: "Email envoyé",
        description: "Vérifiez votre boîte de réception pour réinitialiser votre mot de passe.",
      });
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-secondary/30 p-4">
      <div className="w-full max-w-md space-y-8 animate-in fade-in zoom-in duration-500">
        <div className="flex flex-col items-center justify-center text-center space-y-2">
          <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center shadow-lg shadow-primary/20 mb-4">
            <Bot className="w-8 h-8 text-primary-foreground" />
          </div>
          <h1 className="text-3xl font-display font-bold tracking-tight text-foreground" data-testid="text-login-title">
            {forgotMode ? "Mot de passe oublié" : "Bienvenue"}
          </h1>
          <p className="text-muted-foreground text-sm max-w-xs">
            {forgotMode
              ? "Saisissez votre email pour recevoir un lien de réinitialisation."
              : "Connectez-vous pour accéder au portail Jobotech."}
          </p>
        </div>

        <Card className="border-border/50 shadow-xl shadow-black/5">
          {forgotMode ? (
            <>
              <CardHeader>
                <CardTitle>Réinitialisation</CardTitle>
                <CardDescription>Un lien sera envoyé à votre adresse email</CardDescription>
              </CardHeader>
              {resetSent ? (
                <CardContent className="space-y-4">
                  <div className="flex flex-col items-center gap-3 py-4 text-center">
                    <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                      <Mail className="w-6 h-6 text-green-600 dark:text-green-400" />
                    </div>
                    <p className="text-sm text-muted-foreground" data-testid="text-reset-sent">
                      Un email de réinitialisation a été envoyé à <strong>{resetEmail}</strong>.
                      Vérifiez votre boîte de réception et suivez le lien.
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => { setForgotMode(false); setResetSent(false); setResetEmail(""); }}
                    data-testid="button-back-to-login"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Retour à la connexion
                  </Button>
                </CardContent>
              ) : (
                <form onSubmit={handleForgotPassword}>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="reset-email">Adresse email</Label>
                      <Input
                        id="reset-email"
                        type="email"
                        placeholder="nom@entreprise.com"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        required
                        className="h-11"
                        data-testid="input-reset-email"
                      />
                    </div>
                  </CardContent>
                  <CardFooter className="flex flex-col gap-3">
                    <Button
                      type="submit"
                      className="w-full h-11 text-base font-medium shadow-lg shadow-primary/20 transition-all hover:shadow-xl hover:shadow-primary/30"
                      disabled={loading}
                      data-testid="button-send-reset"
                    >
                      {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : "Envoyer le lien"}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      className="w-full text-sm text-muted-foreground"
                      onClick={() => { setForgotMode(false); setResetEmail(""); }}
                      data-testid="button-cancel-reset"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Retour à la connexion
                    </Button>
                  </CardFooter>
                </form>
              )}
            </>
          ) : (
            <>
              <CardHeader>
                <CardTitle>Connexion</CardTitle>
                <CardDescription>Utilisez votre compte autorisé</CardDescription>
              </CardHeader>
              <form onSubmit={handleLogin}>
                <CardContent className="space-y-4">
                  {/* Message d'erreur OAuth */}
                  {oauthError && (
                    <div className="flex items-start gap-2 bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2.5" data-testid="text-oauth-error">
                      <AlertCircle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
                      <p className="text-sm text-destructive">{oauthError}</p>
                    </div>
                  )}

                  {/* Bouton Google */}
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-11 gap-3 font-medium border-border/60 bg-white dark:bg-background hover:bg-secondary/60 text-foreground"
                    onClick={handleGoogleLogin}
                    disabled={googleLoading}
                    data-testid="button-google-login"
                  >
                    {googleLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <SiGoogle className="w-4 h-4 text-[#4285F4]" />
                    )}
                    Continuer avec Google
                  </Button>

                  {/* Séparateur */}
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-border/50" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-card px-2 text-muted-foreground">ou</span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Adresse email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="nom@entreprise.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="h-11"
                      data-testid="input-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password">Mot de passe</Label>
                      <button
                        type="button"
                        className="text-xs text-primary hover:underline focus:outline-none"
                        onClick={() => setForgotMode(true)}
                        data-testid="link-forgot-password"
                      >
                        Mot de passe oublié ?
                      </button>
                    </div>
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="h-11"
                      data-testid="input-password"
                    />
                  </div>
                </CardContent>
                <CardFooter>
                  <Button
                    type="submit"
                    className="w-full h-11 text-base font-medium shadow-lg shadow-primary/20 transition-all hover:shadow-xl hover:shadow-primary/30"
                    disabled={loading}
                    data-testid="button-login"
                  >
                    {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : "Se connecter"}
                  </Button>
                </CardFooter>
              </form>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
